/**
 * One support request: the thread, and the reply box (CLAUDE.md 12.3).
 *
 * This page is the reason support is a section and not a form: it is where the
 * engineer reads the answer. A resolved thread says that replying reopens it; a
 * closed one says so and points at a new request instead of offering a box nobody
 * will read.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Lock, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import {
  attachmentFilePath,
  supportKeys,
  uploadAttachments,
  useRemoveAttachment,
  useReplyToTicket,
  useTicket,
  useTicketLimits,
  type TicketAttachment,
} from '@/api/support'
import { useAuth } from '@/auth/AuthProvider'
import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { AttachmentPicker, useAttachmentDraft } from '@/components/support/AttachmentPicker'
import { MarkdownEditor } from '@/components/support/MarkdownEditor'
import { TicketStatusPill, TicketTypeIcon } from '@/components/support/TicketBadges'
import { TicketThread } from '@/components/support/TicketThread'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { formatDateTime } from '@/lib/format'
import { NotFoundPage } from '@/pages/NotFoundPage'

export function SupportTicketPage() {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const params = useParams<{ ticketId: string }>()
  const ticketId = Number(params.ticketId)

  const ticket = useTicket(Number.isFinite(ticketId) ? ticketId : null)
  const limits = useTicketLimits()
  const draft = useAttachmentDraft(limits.data)
  const reply = useReplyToTicket(ticketId)
  const remove = useRemoveAttachment(ticketId)

  const [body, setBody] = useState('')
  const [uploading, setUploading] = useState(false)
  const busy = reply.isPending || uploading

  if (ticket.isError) return <NotFoundPage />

  const send = async () => {
    if (busy || !body.trim()) return
    let messageId: number
    try {
      const message = await reply.mutateAsync(body)
      messageId = message.id
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
      return
    }
    if (draft.files.length > 0) {
      setUploading(true)
      try {
        await uploadAttachments(ticketId, draft.files, { messageId })
      } catch (error) {
        toast({
          title: t('support.attachments.upload_failed'),
          description: error instanceof ApiError ? error.localised : undefined,
          variant: 'error',
        })
      } finally {
        setUploading(false)
        void queryClient.invalidateQueries({ queryKey: supportKeys.all })
      }
    }
    setBody('')
    draft.clear()
  }

  const onRemove = (attachment: TicketAttachment) =>
    remove.mutate(attachment.id, {
      onError: (error) =>
        toast({
          title: t('common.error'),
          description: error instanceof ApiError ? error.localised : undefined,
          variant: 'error',
        }),
    })

  const data = ticket.data

  return (
    <WorkspaceChrome title={data?.title ?? t('support.title')}>
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <Link
          to="/support"
          className="inline-flex h-9 items-center gap-1 text-sm text-text-muted hover:text-text"
        >
          <ChevronLeft size={15} aria-hidden />
          {t('support.title')}
        </Link>

        {!data && <Skeleton className="h-64" />}

        {data && (
          <>
            <header className="space-y-2">
              <div className="flex flex-wrap items-start gap-2">
                <h2 className="min-w-0 flex-1 text-lg font-semibold leading-snug">{data.title}</h2>
                <TicketStatusPill status={data.status} className="mt-1" />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
                <span className="inline-flex items-center gap-1">
                  <TicketTypeIcon type={data.type} size={13} />
                  {t(`support.type.${data.type}`)}
                </span>
                <span>
                  {t('support.thread.opened', {
                    date: formatDateTime(data.created_at, i18n.language),
                  })}
                </span>
                {data.module_id && (
                  <span className="rounded bg-surface-raised px-1.5 py-0.5 font-mono text-[11px]">
                    {data.module_id}
                  </span>
                )}
              </div>
            </header>

            <TicketThread
              ticket={data}
              reporterName={user?.display_name ?? ''}
              filePath={(attachment, inline) =>
                attachmentFilePath(ticketId, attachment.id, { inline })
              }
              canRemove={(attachment) => data.can_reply && attachment.uploader_id === user?.id}
              onRemove={onRemove}
            />

            {data.position_snapshot && (
              <details className="rounded-lg border border-border bg-surface p-3">
                <summary className="cursor-pointer text-sm font-medium">
                  {t('support.snapshot.title')}
                </summary>
                <p className="mt-1 text-xs text-text-muted">{t('support.snapshot.hint')}</p>
                <pre className="mt-2 max-h-80 overflow-auto rounded bg-surface-raised p-2 font-mono text-xs">
                  {JSON.stringify(data.position_snapshot, null, 2)}
                </pre>
              </details>
            )}

            {data.can_reply ? (
              <Card>
                <CardBody className="space-y-3">
                  <h3 className="text-sm font-semibold">{t('support.reply')}</h3>
                  {data.status === 'resolved' && (
                    <p className="text-xs text-text-muted">{t('support.thread.reopen_hint')}</p>
                  )}
                  <MarkdownEditor
                    value={body}
                    onChange={setBody}
                    onPasteFiles={draft.add}
                    onSubmit={() => void send()}
                    rows={5}
                    placeholder={t('support.reply_placeholder')}
                    disabled={busy}
                  />
                  <AttachmentPicker draft={draft} disabled={busy} compact />
                  <div className="flex justify-end">
                    <Button
                      variant="primary"
                      loading={busy}
                      disabled={!body.trim()}
                      onClick={() => void send()}
                    >
                      <Send size={15} aria-hidden />
                      {uploading ? t('support.attachments.uploading') : t('support.reply')}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <Card>
                <CardBody className="flex flex-wrap items-center gap-3 text-sm">
                  <Lock size={16} className="text-text-muted" aria-hidden />
                  <span className="min-w-0 flex-1">{t('support.thread.closed_notice')}</span>
                  <Button asChild size="sm">
                    <Link to={`/support/new?ref=${data.id}`}>{t('support.new')}</Link>
                  </Button>
                </CardBody>
              </Card>
            )}
          </>
        )}
      </div>
    </WorkspaceChrome>
  )
}
